import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { RequireAuth } from "@/components/layout/RequireAuth";
import Home from "@/pages/Home";
import CategoryPage from "@/pages/CategoryPage";
import SearchPage from "@/pages/SearchPage";
import ProviderProfile from "@/pages/ProviderProfile";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import ChooseRole from "@/pages/ChooseRole";
import RequestWizard from "@/pages/RequestWizard";
import MyRequests from "@/pages/MyRequests";
import RequestDetail from "@/pages/RequestDetail";
import ProviderEditor from "@/pages/ProviderEditor";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import MyQuotes from "@/pages/MyQuotes";
import Credits from "@/pages/Credits";
import Chats from "@/pages/Chats";
import Account from "@/pages/Account";
import ProviderPackages from "@/pages/ProviderPackages";
import BookPackage from "@/pages/BookPackage";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Checkout from "@/pages/Checkout";
import Earnings from "@/pages/Earnings";
import Notifications from "@/pages/Notifications";
import { AdminLayout } from "@/components/admin/AdminUI";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminVerification from "@/pages/admin/AdminVerification";
import { AdminUsers, AdminUserDetail } from "@/pages/admin/AdminUsers";
import { AdminOrders, AdminOrderDetail } from "@/pages/admin/AdminOrders";
import { AdminPayouts } from "@/pages/admin/AdminPayouts";
import { AdminTransactions, AdminReviews, AdminSettings } from "@/pages/admin/AdminMisc";

const C = ({ children }) => <RequireAuth roles={["pelanggan"]}>{children}</RequireAuth>;
const P = ({ children }) => <RequireAuth roles={["penyedia"]}>{children}</RequireAuth>;
const Any = ({ children }) => <RequireAuth roles={["pelanggan", "penyedia"]}>{children}</RequireAuth>;

function RoleGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user && !user.role && location.pathname !== "/pilih-peran") return <Navigate to="/pilih-peran" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <RoleGate>
      <Header />
      <main className="min-h-[70vh]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/kategori/:slug" element={<CategoryPage />} />
          <Route path="/cari" element={<SearchPage />} />
          <Route path="/penyedia/:slug" element={<ProviderProfile />} />
          <Route path="/masuk" element={<Login />} />
          <Route path="/daftar" element={<Register />} />
          <Route path="/pilih-peran" element={<ChooseRole />} />
          <Route path="/minta-penawaran" element={<C><RequestWizard /></C>} />
          <Route path="/permintaan-saya" element={<C><MyRequests /></C>} />
          <Route path="/permintaan/:id" element={<C><RequestDetail /></C>} />
          <Route path="/dasbor/profil" element={<P><ProviderEditor /></P>} />
          <Route path="/dasbor/permintaan-masuk" element={<P><Leads /></P>} />
          <Route path="/dasbor/permintaan-masuk/:id" element={<P><LeadDetail /></P>} />
          <Route path="/dasbor/penawaran" element={<P><MyQuotes /></P>} />
          <Route path="/dasbor/kredit" element={<P><Credits /></P>} />
          <Route path="/pesan" element={<Any><Chats /></Any>} />
          <Route path="/pesan/:threadId" element={<Any><Chats /></Any>} />
          <Route path="/akun" element={<Any><Account /></Any>} />
          <Route path="/dasbor/paket" element={<P><ProviderPackages /></P>} />
          <Route path="/dasbor/penghasilan" element={<P><Earnings /></P>} />
          <Route path="/pesan-paket/:packageId" element={<C><BookPackage /></C>} />
          <Route path="/pesanan" element={<Any><Orders /></Any>} />
          <Route path="/pesanan/:id" element={<Any><OrderDetail /></Any>} />
          <Route path="/bayar" element={<Any><Checkout /></Any>} />
          <Route path="/notifikasi" element={<Any><Notifications /></Any>} />
          <Route path="/admin" element={<RequireAuth roles={["admin"]}><AdminLayout /></RequireAuth>}>
            <Route index element={<AdminDashboard />} />
            <Route path="kategori" element={<AdminCatalog />} />
            <Route path="pengguna" element={<AdminUsers />} />
            <Route path="pengguna/:id" element={<AdminUserDetail />} />
            <Route path="verifikasi" element={<AdminVerification />} />
            <Route path="pesanan" element={<AdminOrders />} />
            <Route path="pesanan/:id" element={<AdminOrderDetail />} />
            <Route path="pencairan" element={<AdminPayouts />} />
            <Route path="transaksi" element={<AdminTransactions />} />
            <Route path="ulasan" element={<AdminReviews />} />
            <Route path="pengaturan" element={<AdminSettings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </RoleGate>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
